import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, TruckIcon, Scale, TrendingDown, ClipboardList, X, FileDown, AlertTriangle, Mail, FileWarning, Droplets, Package, Award } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import { GrafikHarian } from "../components/GrafikHarian";

const fmt = (v, dec = 0) => (v == null ? "-" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: dec, minimumFractionDigits: 0 }));

const StatCard = ({ title, value, sub, icon: Icon, accent, testId }) => (
  <Card className="border-zinc-200 shadow-sm">
    <CardContent className="p-5 flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{title}</p>
        <p data-testid={testId} className="text-2xl font-semibold tracking-tight text-zinc-900 mt-1.5 tabular-nums">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
    </CardContent>
  </Card>
);

const AggTable = ({ title, rows, testId }) => (
  <Card className="border-zinc-200 shadow-sm">
    <CardHeader className="pb-3"><CardTitle className="text-base font-semibold tracking-tight">{title}</CardTitle></CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testId}>
          <thead>
            <tr className="border-y border-zinc-200 bg-zinc-50">
              {["Nama", "Trip", "Muat (KG)", "Susut Total (KG)", "Susut Rata²  (KG)"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-zinc-400">Belum ada data</td></tr>}
            {rows.map((r) => (
              <tr key={r.nama} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                <td className="px-4 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{r.nama}</td>
                <td className="px-4 py-2.5 tabular-nums">{r.trips}</td>
                <td className="px-4 py-2.5 tabular-nums">{fmt(r.muat, 2)}</td>
                <td className={`px-4 py-2.5 tabular-nums ${r.susut_total > 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(r.susut_total, 2)}</td>
                <td className={`px-4 py-2.5 tabular-nums font-medium ${r.susut_avg > 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(r.susut_avg, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [docAlerts, setDocAlerts] = useState(null);
  const [oliAlerts, setOliAlerts] = useState(null);
  const [restockAlerts, setRestockAlerts] = useState(null);
  const [supirRank, setSupirRank] = useState(null);
  const [supirDays, setSupirDays] = useState(30);

  useEffect(() => {
    setSupirRank(null);
    api.get(`/dashboard/supir-terbaik?days=${supirDays}`).then(({ data }) => setSupirRank(data)).catch(() => setSupirRank({ rows: [] }));
  }, [supirDays]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const sendEmailNow = async () => {
    setSendingEmail(true);
    try {
      const { data } = await api.post("/notifications/send");
      toast.success(`Email notifikasi terkirim ke ${data.recipient} (${data.count} trip)`);
    } catch (err) {
      toast.error("Gagal mengirim email notifikasi");
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    api.get("/trips/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/kendaraan/alerts").then(({ data }) => setDocAlerts(data)).catch(() => {});
    api.get("/servis/alerts").then(({ data }) => setOliAlerts(data)).catch(() => {});
    api.get("/gudang/alerts").then(({ data }) => setRestockAlerts(data)).catch(() => {});
  }, []);

  const downloadReport = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/dashboard/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Laporan_Ringkasan_Trip.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Laporan PDF diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh laporan");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      api.get("/dashboard", { params }).then(({ data }) => setData(data)).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [dateFrom, dateTo]);

  const monthData = useMemo(() => (data ? data.per_month : []), [data]);
  const komoditasData = useMemo(() => (data ? data.per_komoditas : []), [data]);

  if (!data)
    return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const t = data.totals;
  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Dashboard Ringkasan</h1>
          <p className="text-sm text-zinc-500 mt-1">Performa perjalanan truk berdasarkan periode tanggal muat</p>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="dashboard-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white sm:w-40" />
          <span className="text-xs text-zinc-400">s/d</span>
          <Input data-testid="dashboard-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white sm:w-40" />
          {(dateFrom || dateTo) && (
            <Button data-testid="dashboard-reset-dates" variant="outline" size="icon" onClick={() => { setDateFrom(""); setDateTo(""); }} className="border-zinc-300 shrink-0">
              <X className="w-4 h-4" />
            </Button>
          )}
          <Button data-testid="download-report-button" onClick={downloadReport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 shrink-0">
            <FileDown className="w-4 h-4 mr-2" /> Laporan PDF
          </Button>
        </div>
      </div>

      {alerts && alerts.count > 0 && (
        <div data-testid="stale-trips-alert" className="bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                {alerts.count} trip masih berstatus "Berangkat" lebih dari {alerts.threshold_days} hari
              </p>
              <ul className="mt-2 space-y-1">
                {alerts.trips.slice(0, 5).map((t) => (
                  <li key={t.id} data-testid={`stale-trip-${t.ID_Trip}`} className="text-sm text-amber-800">
                    <span className="font-medium">{t.ID_Trip}</span> — {t.Plat_Truk}, {t.Nama_Supir} → {t.Tujuan_Bongkar}
                    <span className="text-amber-600"> ({t.hari_berjalan} hari sejak muat {t.Tanggal_Muat})</span>
                  </li>
                ))}
                {alerts.trips.length > 5 && <li className="text-sm text-amber-700">+{alerts.trips.length - 5} trip lainnya</li>}
              </ul>
              <div className="mt-2 flex items-center gap-4">
                <button data-testid="alert-goto-trips" onClick={() => navigate("/trips")}
                  className="text-sm font-medium text-amber-900 underline hover:text-amber-700 transition-colors duration-200">
                  Lihat daftar trip →
                </button>
                {user?.role === "owner" && (
                  <button data-testid="send-email-now-button" onClick={sendEmailNow} disabled={sendingEmail}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 border border-amber-400 rounded-md px-2.5 py-1 hover:bg-amber-100 transition-colors duration-200 disabled:opacity-50">
                    {sendingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    Kirim Email Sekarang
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-amber-700">Email notifikasi otomatis dikirim setiap hari ke admin selama masih ada trip tertunda.</p>
            </div>
          </div>
        </div>
      )}

      {restockAlerts && restockAlerts.count > 0 && (
        <div data-testid="restock-alert" className="bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-800">
                WAKTUNYA RESTOCK — {restockAlerts.count} suku cadang menyentuh batas minimum stok
              </p>
              <ul className="mt-2 space-y-1">
                {restockAlerts.items.slice(0, 5).map((b) => (
                  <li key={b.Kode_Barang} data-testid={`restock-item-${b.Kode_Barang}`} className="text-sm text-red-700">
                    <span className="font-semibold">{b.Nama_Barang}</span> ({b.Kode_Barang}, {b.Kategori_Barang}) — sisa <b>{b.Stok_Tersedia}</b>, batas minimum {b.Batas_Minimum_Stok}
                  </li>
                ))}
                {restockAlerts.items.length > 5 && <li className="text-sm text-red-600">+{restockAlerts.items.length - 5} barang lainnya</li>}
              </ul>
              {user?.role === "owner" && (
                <button data-testid="restock-goto-gudang" onClick={() => navigate("/gudang")}
                  className="mt-2 text-sm font-medium text-red-800 underline hover:text-red-600 transition-colors duration-200">
                  Kelola stok di Inventaris Gudang →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {oliAlerts && oliAlerts.count > 0 && (
        <div data-testid="ganti-oli-alert" className="bg-yellow-50 border border-yellow-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Droplets className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-yellow-900">
                WAKTUNYA GANTI OLI — {oliAlerts.count} truk sudah menempuh &ge; {Number(oliAlerts.interval_km).toLocaleString("id-ID")} KM sejak servis terakhir
              </p>
              <ul className="mt-2 space-y-1">
                {oliAlerts.items.slice(0, 5).map((a) => (
                  <li key={a.Plat_Truk} data-testid={`oli-alert-${a.Plat_Truk.replace(/ /g, "-")}`} className="text-sm text-yellow-800">
                    <span className="font-semibold">{a.Plat_Truk}</span> — odometer saat ini {Number(a.Odometer_Terkini_KM).toLocaleString("id-ID")} KM, servis terakhir di {Number(a.Odometer_Servis_Terakhir_KM).toLocaleString("id-ID")} KM
                    <span className="text-yellow-600"> (+{Number(a.Selisih_KM).toLocaleString("id-ID")} KM)</span>
                  </li>
                ))}
                {oliAlerts.items.length > 5 && <li className="text-sm text-yellow-700">+{oliAlerts.items.length - 5} truk lainnya</li>}
              </ul>
              {user?.role === "owner" && (
                <button data-testid="oli-alert-goto-armada" onClick={() => navigate("/armada")}
                  className="mt-2 text-sm font-medium text-yellow-900 underline hover:text-yellow-700 transition-colors duration-200">
                  Catat servis di Armada &amp; Servis →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {docAlerts && docAlerts.count > 0 && (
        <div data-testid="doc-expiry-alert" className="bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileWarning className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">
                {docAlerts.count} dokumen kendaraan kedaluwarsa / habis masa berlaku &lt; {docAlerts.threshold_days} hari
              </p>
              <ul className="mt-2 space-y-1">
                {docAlerts.items.slice(0, 5).map((d, i) => (
                  <li key={i} data-testid={`doc-alert-${d.Plat_Nomor.replace(/ /g, "-")}-${d.dokumen}`} className="text-sm text-red-800">
                    <span className="font-medium">{d.Plat_Nomor}</span> — {d.dokumen} berlaku s/d {d.tanggal}{" "}
                    <span className={d.hari_tersisa < 0 ? "font-semibold" : "text-red-600"}>
                      ({d.hari_tersisa < 0 ? `kedaluwarsa ${Math.abs(d.hari_tersisa)} hari lalu` : `${d.hari_tersisa} hari lagi`})
                    </span>
                  </li>
                ))}
                {docAlerts.items.length > 5 && <li className="text-sm text-red-700">+{docAlerts.items.length - 5} dokumen lainnya</li>}
              </ul>
              {user?.role === "owner" && (
                <button data-testid="doc-alert-goto-master" onClick={() => navigate("/master-data")}
                  className="mt-2 text-sm font-medium text-red-900 underline hover:text-red-700 transition-colors duration-200">
                  Perbarui di Master Data →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard testId="stat-trip-count" title="Total Trip" value={fmt(t.trip_count)} icon={ClipboardList} accent="bg-emerald-100 text-emerald-700" />
        <StatCard testId="stat-tonase-muat" title="Total Tonase Muat" value={`${fmt(t.tonase_muat, 2)} KG`} icon={TruckIcon} accent="bg-blue-100 text-blue-700" />
        <StatCard testId="stat-tonase-bongkar" title="Total Tonase Bongkar" value={`${fmt(t.tonase_bongkar, 2)} KG`} icon={Scale} accent="bg-amber-100 text-amber-700" />
        <StatCard testId="stat-penyusutan" title="Total Penyusutan" value={`${fmt(t.penyusutan_total, 2)} KG`}
          sub={t.penyusutan_avg != null ? `Rata-rata ${fmt(t.penyusutan_avg, 2)} KG / trip` : null} icon={TrendingDown} accent="bg-red-100 text-red-700" />
      </div>

      <GrafikHarian prefix="dash" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold tracking-tight">Tonase per Bulan</CardTitle></CardHeader>
          <CardContent className="h-72" data-testid="chart-per-bulan">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={monthData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="bulan" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("id-ID")} />
                <Tooltip formatter={(v) => `${Number(v).toLocaleString("id-ID")} KG`} />
                <Legend />
                <Bar dataKey="muat" name="Muat (KG)" fill="#047857" radius={[3, 3, 0, 0]} />
                <Bar dataKey="bongkar" name="Bongkar (KG)" fill="#93C5FD" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-semibold tracking-tight">Tonase per Komoditas</CardTitle></CardHeader>
          <CardContent className="h-72" data-testid="chart-per-komoditas">
            <ResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={komoditasData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="nama" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("id-ID")} />
                <Tooltip formatter={(v) => `${Number(v).toLocaleString("id-ID")} KG`} />
                <Legend />
                <Bar dataKey="muat" name="Muat (KG)" fill="#047857" radius={[3, 3, 0, 0]} />
                <Bar dataKey="bongkar" name="Bongkar (KG)" fill="#93C5FD" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AggTable title="Ringkasan per Truk" rows={data.per_truck} testId="table-per-truk" />
        <AggTable title="Ringkasan per Supir" rows={data.per_driver} testId="table-per-supir" />
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-700" /> Supir Terbaik — {supirDays} Hari Terakhir
            </CardTitle>
            <div className="flex gap-1" data-testid="supir-days-filter">
              {[30, 90, 365].map((d) => (
                <button key={d} data-testid={`supir-days-${d}`} onClick={() => setSupirDays(d)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors duration-200 ${supirDays === d ? "bg-emerald-700 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                  {d} hari
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-zinc-500">Peringkat berdasarkan jumlah trip terbanyak dan rata-rata susut terkecil</p>
        </CardHeader>
        <CardContent data-testid="chart-supir-terbaik">
          {!supirRank ? (
            <div className="flex justify-center py-8 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : supirRank.rows.length === 0 ? (
            <p data-testid="supir-terbaik-empty" className="text-sm text-zinc-400 text-center py-6">Belum ada trip pada periode ini.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={supirRank.rows} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="nama" width={110} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v, name) => name === "Trip" ? [`${v} trip`, "Jumlah Trip"] : v} />
                    <Bar dataKey="trip" name="Trip" fill="#047857" radius={[0, 3, 3, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="w-full text-sm" data-testid="supir-terbaik-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Supir</th>
                    <th className="py-2 pr-2 text-right">Trip</th>
                    <th className="py-2 pr-2 text-right">Tonase Muat</th>
                    <th className="py-2 text-right">Rata-rata Susut</th>
                  </tr>
                </thead>
                <tbody>
                  {supirRank.rows.map((r, i) => (
                    <tr key={r.nama} data-testid={`supir-rank-${i + 1}`} className="border-b border-zinc-100">
                      <td className="py-2 pr-2">
                        <span className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-800" : i === 1 ? "bg-zinc-200 text-zinc-700" : i === 2 ? "bg-orange-100 text-orange-800" : "text-zinc-500"}`}>{i + 1}</span>
                      </td>
                      <td className="py-2 pr-2 font-medium text-zinc-900">{r.nama}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{r.trip}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{fmt(r.tonase_muat, 2)} KG</td>
                      <td className="py-2 text-right tabular-nums">{r.rata_susut == null ? "—" : `${fmt(r.rata_susut, 2)} KG`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold tracking-tight flex items-center justify-between gap-2">
              <span>Biaya Servis per Bulan (per Truk)</span>
              <span data-testid="servis-total-biaya" className="text-sm font-semibold text-emerald-800 tabular-nums">
                Total: Rp {fmt(data.servis?.total_biaya)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="h-72" data-testid="chart-biaya-servis">
            {(data.servis?.chart || []).length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-zinc-400">Belum ada riwayat servis pada periode ini</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={data.servis.chart} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="bulan" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("id-ID")} />
                  <Tooltip formatter={(v) => `Rp ${Number(v).toLocaleString("id-ID")}`} />
                  <Legend />
                  {data.servis.plats.map((p, i) => (
                    <Bar key={p} dataKey={p} name={p} stackId="servis"
                      fill={["#047857", "#93C5FD", "#F59E0B", "#8B5CF6", "#EF4444", "#0EA5E9"][i % 6]}
                      radius={i === data.servis.plats.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-3"><CardTitle className="text-base font-semibold tracking-tight">Rincian Biaya Servis per Truk per Bulan</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-biaya-servis">
                <thead>
                  <tr className="border-y border-zinc-200 bg-zinc-50">
                    {["Bulan", "Plat Truk", "Jumlah Servis", "Total Biaya"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.servis?.rows || []).length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-400">Belum ada riwayat servis</td></tr>
                  )}
                  {(data.servis?.rows || []).map((r) => (
                    <tr key={`${r.bulan}-${r.plat}`} data-testid={`servis-biaya-row-${r.bulan}-${r.plat.replace(/ /g, "-")}`}
                      className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                      <td className="px-4 py-2.5 whitespace-nowrap">{r.bulan}</td>
                      <td className="px-4 py-2.5 font-medium text-zinc-900 whitespace-nowrap">{r.plat}</td>
                      <td className="px-4 py-2.5 tabular-nums text-center">{r.jumlah_servis}</td>
                      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap font-medium">Rp {fmt(r.total_biaya)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
